import { Link, useLocation } from 'react-router-dom';
import { DT, DS2 } from '../../theme/designTokens';
import { SIDEBAR_NAV_ITEMS } from '../../config/navigation';
import { ASSETS } from '../../config/assets';
import { useFriends } from '../../context/FriendContext';
import { useTranslation } from '../../i18n/react';
import { PlayerAvatar } from '../ui/PlayerAvatar';
import { UnreadBadge } from '../ui/UnreadBadge';
import { LanguageSelector } from '../ui/LanguageSelector';
import { ChatSidebarModule } from '../chat/dm/ChatSidebarModule';

// Maps each SIDEBAR_NAV_ITEMS icon key to its approved Figma-exported PNG.
// These are the only nav icon assets in scope — no generic SVG fallback.
// Exported for the DS2 mobile Drawer variant, which uses the same icon set.
export const NAV_ICON_ASSET: Record<string, string> = {
  play:        ASSETS.navigation.PLAY_ONLINE,
  bot:         ASSETS.navigation.PLAY_BOTS,
  leaderboard: ASSETS.navigation.LEADERBOARD,
  friends:     ASSETS.navigation.FRIENDS,
  rules:       ASSETS.navigation.RULES,
  settings:    ASSETS.navigation.SETTINGS,
};

function MessagesNavIcon({ size, color }: { size: number; color: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3h11A2.5 2.5 0 0 1 20 5.5v8A2.5 2.5 0 0 1 17.5 16H9l-4.5 4v-4H6.5" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function AchievementsNavIcon({ size, color }: { size: number; color: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M7 4h10v4a5 5 0 0 1-10 0V4Z" stroke={color} strokeWidth="1.7" strokeLinejoin="round" />
      <path d="M7 5H4v1.5A3.5 3.5 0 0 0 7.5 10" stroke={color} strokeWidth="1.7" strokeLinecap="round" />
      <path d="M17 5h3v1.5A3.5 3.5 0 0 1 16.5 10" stroke={color} strokeWidth="1.7" strokeLinecap="round" />
      <path d="M12 13v3M9 20h6M9.5 20c0-2 .8-3 2.5-3s2.5 1 2.5 3" stroke={color} strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

interface SidebarUser {
  displayName: string;
  /** Selected preset avatar key (Player Identity V1); absent → initial. */
  avatarKey?: string | null;
  /** Legacy provider-photo placeholder — backend always sends null today. */
  avatarUrl?: string;
}

interface Props {
  /** 'full' = desktop (icon + label). 'collapsed' = tablet (icon only). */
  variant: 'full' | 'collapsed';
  /** Present = logged-in chrome (profile chip + Log Out). Absent = logged-out chrome (Sign Up/Log In). */
  user?: SidebarUser | null;
}

// Chess-calibrated sidebar (Option A experiment): production gaming sites
// (chess.com measured at ~11% of viewport, icons ~12% of sidebar width) run
// a much lighter sidebar than Figma's 15%/17.8% spec. These values keep all
// DS2 colors/fonts/assets but adopt the production-grade sizing skeleton.
const SIDEBAR_WIDTH_FULL = 190;
const SIDEBAR_WIDTH_COLLAPSED = 72;

const ICON_SIZE_FULL = 26;
const ICON_SIZE_COLLAPSED = 32;

/**
 * Design System V2 — persistent app sidebar.
 *
 * Matches the approved Figma sidebar (Landing / Get Started / Home /
 * Leaderboard / Friends screenshots): logo, primary nav, then either
 * Sign Up + Log In (logged-out) or a profile chip + Log Out (logged-in),
 * with a Help & Support link pinned to the very bottom.
 *
 * Rendered on tablet (collapsed) and desktop (full) only — mobile keeps
 * the existing TopNav + Drawer pattern, per DS2 responsive rules.
 */
export function Sidebar({ variant, user }: Props) {
  const { pathname } = useLocation();
  const { unreadMessageCount } = useFriends();
  // Nav labels resolve at render time (never at module import) so runtime
  // language switching re-labels the sidebar immediately.
  const { t } = useTranslation('navigation');
  const { t: tc } = useTranslation('common');
  const isActive = (path: string) => pathname === path || pathname.startsWith(path + '/');
  const full = variant === 'full';
  const width = full ? SIDEBAR_WIDTH_FULL : SIDEBAR_WIDTH_COLLAPSED;

  // Docked Chat Redesign (desktop slice): the FULL (desktop) sidebar never
  // shows a plain Messages/Chat nav row, logged in OR out — a logged-in
  // visitor gets the integrated Chat flyout below instead, and a logged-out
  // visitor simply has no chat surface pre-signup, matching that same
  // minimal design exactly. This is a presentation-specific filter —
  // SIDEBAR_NAV_ITEMS itself is untouched, so the tablet collapsed sidebar
  // and the mobile Drawer keep their existing Messages entry regardless of
  // login state (the mobile dock replacement has not been built yet).
  const hideMessagesRow = full;
  const showChatModule = full && Boolean(user);
  const navItems = hideMessagesRow
    ? SIDEBAR_NAV_ITEMS.filter(item => item.icon !== 'messages')
    : SIDEBAR_NAV_ITEMS;

  return (
    <nav
      style={{
        width,
        flexShrink:    0,
        height:        '100%',
        display:       'flex',
        flexDirection: 'column',
        background:    DS2.color.surface,
        borderRight:   '1px solid #CB9719',
        // Full-height panel flush against the real screen edges on native
        // (tablet landscape notch / status bar top, gesture bar bottom) —
        // env() resolves to 0 with no inset, so this is a no-op on web.
        paddingTop:    `calc(${full ? 24 : 32}px + ${DT.layout.safeAreaTop})`,
        paddingBottom: `calc(${full ? 24 : 32}px + ${DT.layout.safeAreaBottom})`,
        paddingLeft:   full ? 20 : 12,
        paddingRight:  full ? 20 : 12,
        boxSizing:     'border-box',
        overflowY:     'auto',
      }}
    >
      {/* Logo — approved Figma sidebar lockup (210×234 reference, near-square),
          compact chess-calibrated size, centered. */}
      <Link
        to="/"
        style={{
          display:        'flex',
          justifyContent: 'center',
          marginBottom:   28,
          textDecoration: 'none',
        }}
      >
        <img
          src={ASSETS.brand.LOGO_SIDEBAR}
          alt="The Domino Game"
          style={{ width: full ? 90 : 40, height: 'auto', objectFit: 'contain' }}
          draggable={false}
        />
      </Link>

      {/* Profile chip (logged-in only) — links to /settings, where the user
          manages their profile. Avatar circle fills the chip height (Figma
          Frame 25: 73px avatar in an 88px pill ≈ 83%) — it will hold the
          user's photo once profile pictures are wired. */}
      {user && (
        <Link
          to="/settings"
          title={t('aria.settings')}
          data-tutorial-target="profile-chip"
          style={{
            display:        'flex',
            alignItems:     'center',
            justifyContent: full ? 'flex-start' : 'center',
            gap:            10,
            padding:        full ? '4px 14px 4px 4px' : '4px',
            marginBottom:   20,
            // Figma HOME frame (Frame 25): pill chip, #F5F5F5 avatar circle,
            // WHITE Montserrat name on the gold gradient — not dark-on-gold.
            borderRadius:   999,
            background:     DS2.gradient.gold,
            boxShadow:      DT.shadow.gold,
            textDecoration: 'none',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {/* Identity order: [avatar] [name]. The country flag was removed
              from THIS card only (product decision) — the reclaimed width goes
              to the username. Every other identity surface (lobby seats,
              leaderboard, friends, game overlays, …) keeps its flag. */}
          <PlayerAvatar
            avatarKey={user.avatarKey}
            name={user.displayName}
            size={full ? 44 : 40}
            loading="eager"
            initialStyle={{
              // Preserve the approved Figma chip look: #F5F5F5 circle,
              // dark heading-font initial.
              background: '#F5F5F5',
              border:     'none',
              color:      '#0b0b0b',
              fontWeight: 700,
              fontSize:   full ? 18 : 16,
              fontFamily: DS2.font.heading,
            }}
          />
          {full && (
            <span title={user.displayName} style={{ fontSize: 13, fontWeight: 700, color: '#FFFFFF', fontFamily: DS2.font.heading, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user.displayName}
            </span>
          )}
        </Link>
      )}

      {/* Primary nav — approved Figma icon set (800×800 source PNGs).
          Chess-calibrated row pitch: 26px icon + 8px gap ≈ 40px per row. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {navItems.map(item => {
          const active = isActive(item.path);
          const label = t(`items.${item.labelKey}`);
          // Active nav label uses a distinct brighter gold-yellow (#FFC844) per
          // Figma's confirmed CSS — not the same hex as DS2.color.gold (#c9a84c),
          // which is used for borders/accents elsewhere.
          const color = active ? '#FFC844' : DS2.color.textBright;
          const iconSrc = NAV_ICON_ASSET[item.icon];
          return (
            <Link
              key={item.path}
              to={item.path}
              title={full ? undefined : label}
              data-tutorial-target={`nav:${item.path}`}
              style={{
                display:        'flex',
                alignItems:     'center',
                justifyContent: full ? 'flex-start' : 'center',
                gap:            8,
                padding:        full ? '4px 0' : '8px 0',
                textDecoration: 'none',
                color,
                fontSize:       full ? 14 : 12,
                fontWeight:     700,
                fontFamily:     DS2.font.heading,
              }}
            >
              {item.icon === 'messages' ? (
                <span style={{ position: 'relative', display: 'flex', flexShrink: 0 }}>
                  <MessagesNavIcon size={full ? ICON_SIZE_FULL : ICON_SIZE_COLLAPSED} color={color} />
                  {!full && unreadMessageCount > 0 && (
                    <span style={{ position: 'absolute', top: -4, right: -4 }}>
                      <UnreadBadge count={unreadMessageCount} />
                    </span>
                  )}
                </span>
              ) : item.icon === 'achievements' ? (
                <AchievementsNavIcon size={full ? ICON_SIZE_FULL : ICON_SIZE_COLLAPSED} color={color} />
              ) : (
                iconSrc && (
                  <img
                    src={iconSrc}
                    alt=""
                    style={{
                      width:     full ? ICON_SIZE_FULL : ICON_SIZE_COLLAPSED,
                      height:    full ? ICON_SIZE_FULL : ICON_SIZE_COLLAPSED,
                      flexShrink: 0,
                      objectFit: 'contain',
                    }}
                    draggable={false}
                  />
                )
              )}
              {full && (
                <span style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center' }}>
                  {label}
                  {item.icon === 'messages' && unreadMessageCount > 0 && <UnreadBadge count={unreadMessageCount} />}
                </span>
              )}
            </Link>
          );
        })}
      </div>

      {/* Spacer — chess-calibrated: auth actions + Help & Support pin to the bottom */}
      <div style={{ flex: 1 }} />

      {/* Docked Chat Redesign: integrated Chat module, pinned above the
          bottom controls (never pushes logout/language/support offscreen —
          its list scrolls independently inside a capped height, and the
          sidebar itself remains scrollable at short viewport heights). The
          module self-hides on suppressed routes and below the dock
          breakpoint. */}
      {showChatModule && <ChatSidebarModule />}

      {/* Auth actions — full-width buttons (chess.com pattern), DS2 styling.
          Docked Chat Redesign: the logged-in sidebar no longer carries a
          Log Out button (approved concept) — logging out lives on the
          Settings screen (desktop/tablet) and in the mobile Drawer, both on
          the same shared useLogout contract. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {!user && (
          <>
            <SidebarGoldButton to="/signup" label={tc('action.signUp')} full={full} />
            <SidebarGhostButton to="/login" label={tc('action.logIn')} full={full} />
            {/* Pre-login language selector — TABLET (collapsed) only. The
                FULL desktop sidebar drops it for logged-out visitors too,
                matching the logged-in design exactly: the app already
                auto-detects a visitor's language from their device, and
                manual switching becomes available in Settings once they
                sign up. Opens upward: this block sits at the panel's
                bottom edge. */}
            {!full && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 4 }}>
                <LanguageSelector variant="compact" openUp iconOnly id="sidebar-language" />
              </div>
            )}
          </>
        )}
        <Link
          to="/help"
          style={{
            marginTop:      24,
            textAlign:      'center',
            fontSize:       11,
            fontWeight:     500,
            fontFamily:     DS2.font.body,
            color:          DS2.color.textBright,
            textDecoration: 'underline',
          }}
        >
          {full ? 'Help & Support' : '?'}
        </Link>
      </div>
    </nav>
  );
}

function SidebarGoldButton({ to, label, full }: { to: string; label: string; full: boolean }) {
  return (
    <Link
      to={to}
      title={full ? undefined : label}
      style={{
        display:        'flex',
        alignItems:     'center',
        justifyContent: 'center',
        width:          '100%',
        minHeight:      full ? 36 : 44,
        padding:        full ? '8px 16px' : '10px 8px',
        // Figma sidebar buttons use ~11-12px radius (1920 export), not DS2.radius.button (16)
        borderRadius:   12,
        background:     DS2.gradient.gold,
        color:          '#FFFFFF',
        fontSize:       full ? 14 : 13,
        fontWeight:     500,
        fontFamily:     DS2.font.body,
        textDecoration: 'none',
        boxSizing:      'border-box',
        boxShadow:      DT.shadow.gold,
      }}
    >
      {full ? label : label.slice(0, 1)}
    </Link>
  );
}

interface SidebarGhostButtonProps {
  label: string;
  full:  boolean;
  /** Nav destination — omit when this button performs an action instead (see onClick). */
  to?: string;
  /** Action handler — renders a <button> instead of a <Link> (e.g. Log Out). */
  onClick?: () => void;
  disabled?: boolean;
}

function SidebarGhostButton({ to, label, full, onClick, disabled }: SidebarGhostButtonProps) {
  const style: React.CSSProperties = {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    width:          '100%',
    minHeight:      full ? 36 : 44,
    padding:        full ? '8px 16px' : '10px 8px',
    borderRadius:   12,
    background:     '#131313',
    border:         `1px solid ${DS2.color.gold}`,
    color:          '#FFFFFF',
    fontSize:       full ? 14 : 13,
    fontWeight:     500,
    fontFamily:     DS2.font.body,
    textDecoration: 'none',
    boxSizing:      'border-box',
    cursor:         disabled ? 'default' : 'pointer',
    opacity:        disabled ? 0.6 : 1,
  };

  const content = full ? label : label.slice(0, 1);

  if (onClick) {
    return (
      <button type="button" onClick={onClick} disabled={disabled} title={full ? undefined : label} style={style}>
        {content}
      </button>
    );
  }

  return (
    <Link to={to!} title={full ? undefined : label} style={style}>
      {content}
    </Link>
  );
}
